import React from 'react'
import { Card, Empty } from 'antd'
import { useTranslation } from 'react-i18next'
import { TrendChart, DistributionChart } from '../charts'
import { PredictionAnalysis } from '../analysis/PredictionAnalysis/PredictionAnalysis'
import { ComparisonAnalysis } from '../analysis/ComparisonAnalysis/ComparisonAnalysis'
import { StatisticsDataType } from '../../types/statistics'

interface StatisticsChartAreaProps {
    chartType: string
    statisticsData: StatisticsDataType | null
    comparisonData: { previous: StatisticsDataType | null }
    versionName?: string
    trendComparisonWindowDays?: number
    businessDate?: string | null
}

export const StatisticsChartArea: React.FC<StatisticsChartAreaProps> = ({
    chartType,
    statisticsData,
    comparisonData,
    versionName,
    trendComparisonWindowDays,
    businessDate,
}) => {
    const { t } = useTranslation('statistics')

    if (!statisticsData) return null
    const speciesSummary = statisticsData.speciesSummary ?? statisticsData
    const effectiveTrendData = statisticsData.trendData ?? []

    return (
        <Card>
            {chartType === 'trend' &&
                (effectiveTrendData.length ? (
                    <TrendChart
                        data={effectiveTrendData}
                        previousTrendData={comparisonData.previous?.trendData}
                        comparisonWindowDays={trendComparisonWindowDays}
                        maxDate={businessDate}
                    />
                ) : (
                    <Empty description={t('empty.noTrendData')} />
                ))}

            {chartType === 'distribution' &&
                (speciesSummary.pestDistribution?.length ? (
                    <DistributionChart
                        data={speciesSummary.pestDistribution}
                    />
                ) : (
                    <Empty description={t('empty.noDistributionData')} />
                ))}

            {chartType === 'prediction' &&
                effectiveTrendData.length > 0 && (
                    <PredictionAnalysis
                        historicalData={effectiveTrendData}
                        versionName={versionName}
                    />
                )}

            {chartType === 'comparison' && (
                <ComparisonAnalysis
                    currentData={{
                        total: statisticsData.totalDetections,
                        byType: statisticsData.pestDistribution.map((item) => ({
                            name: item.name,
                            count: item.value,
                        })),
                    }}
                    previousData={{
                        total: comparisonData?.previous?.totalDetections || 0,
                        byType: (comparisonData?.previous?.pestDistribution || []).map((item) => ({
                            name: item.name,
                            count: item.value,
                        })),
                    }}
                />
            )}
        </Card>
    )
}
